'use client';

import { LandingPageForm, JourneyType } from '../LandingPageForm/LandingPageForm';
import { useIsBreakpoint, Breakpoint } from '@/hooks/useIsBreakpoint';
import TrustPilotWidget, { TrustpilotLayouts } from '../TrustPilot/TrustPilot';

export type LayoutVariant = '1' | '2' | '3';

interface LandingPageHeroProps {
  layout: LayoutVariant;
  heading: string;
  subheading?: string;
  backgroundImage1?: string; // for layout 1 only
  backgroundImage2?: string; // for layouts 2 & 3
  regLabel?: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  useCardStyle?: boolean;
  journey: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker?: boolean;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
}

export const LandingPageHero = ({
  layout = '1',
  heading,
  subheading,
  backgroundImage1,
  backgroundImage2,
  regLabel = 'Enter Registration Number',
  showRegLabel,
  showRegToggle,
  defaultDurationType,
  daysOnly,
  useBanner,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  useCardStyle = true,
  journey,
  regPlateOnly,
  showDurationPicker,
  formIntroText,
  submitButtonText,
  submitButtonColor,
}: LandingPageHeroProps) => {
  const isMobile = useIsBreakpoint(Breakpoint.MD);

  const backgroundImage = layout === '1' ? backgroundImage1 : backgroundImage2;

  const getLayoutClasses = () => {
    switch (layout) {
      case '1':
        // layout 1: spread across full width
        return 'md:grid-cols-[1fr_auto]';
      case '2':
        // layout 2: both on left side, form first
        return 'justify-start md:grid-cols-[auto_auto] md:justify-start';
      case '3':
        // layout 3: both on left side, content first
        return 'justify-start md:grid-cols-[auto_auto] md:justify-start';
      default:
        return 'md:grid-cols-[1fr_auto]';
    }
  };

  const getFormOrder = () => {
    switch (layout) {
      case '1':
        // layout 1: form far right (desktop), form at bottom (mobile)
        return 'order-2 mt-auto md:mt-0';
      case '2':
        // layout 2: form on left (desktop), form after content (mobile)
        return 'order-2 md:order-1';
      case '3':
        // layout 3: form on right of content (desktop), form after content (mobile)
        return 'order-2';
      default:
        return 'order-2 mt-auto md:mt-0';
    }
  };

  const getContentOrder = () => {
    switch (layout) {
      case '1':
        // layout 1: content far left (desktop), content top (mobile)
        return 'order-1';
      case '2':
        // layout 2: content right of form (desktop), content top (mobile)
        return 'order-1 md:order-2';
      case '3':
        // layout 3: content left of form (desktop), content top (mobile)
        return 'order-1';
      default:
        return 'order-1';
    }
  };

  const getGapClasses = () => {
    switch (layout) {
      case '1':
        // layout 1: smaller gap on mobile to show more background
        return 'gap-4 md:gap-x-8';
      case '2':
      case '3':
        // layouts 2 & 3: smaller gap on mobile to stack tightly at top
        return 'gap-3 md:gap-x-8';
      default:
        return 'gap-4 md:gap-x-8';
    }
  };

  const getBackgroundStyle = () => {
    if (!backgroundImage) return {};

    const baseStyle = {
      backgroundImage: `url(${backgroundImage})`,
      backgroundRepeat: 'no-repeat',
    };

    if (!isMobile) {
      return {
        ...baseStyle,
        backgroundSize: 'cover',
        backgroundPosition: '60% center',
      };
    }

    // layout-specific mobile positioning and scaling
    switch (layout) {
      case '1':
        return {
          ...baseStyle,
          backgroundSize: '280%',
          backgroundPosition: '50% 25%',
        };
      case '2':
        return {
          ...baseStyle,
          backgroundSize: '280%',
          backgroundPosition: '97% 98%',
        };
      case '3':
        return {
          ...baseStyle,
          backgroundSize: '280%',
          backgroundPosition: '97% 98%',
        };
      default:
        return {
          ...baseStyle,
          backgroundSize: '240%',
          backgroundPosition: '50% 40%',
        };
    }
  };

  const firstWord = heading.split(' ')[0];
  const restOfHeading = heading.split(' ').slice(1).join(' ');
  const mobileRegLabel = formIntroText || 'To get started simply enter your reg...';
  const desktopRegLabel = regLabel || '';
  const shouldShowDurationPicker = !isMobile && (showDurationPicker !== undefined ? showDurationPicker : true);

  return (
    <section className="relative min-h-[650px] w-full overflow-hidden md:min-h-0">
      {/* Background Image */}
      {backgroundImage && <div className="absolute inset-0 z-0" style={getBackgroundStyle()} aria-label={heading} />}

      {/* Content Container */}
      <div className="relative z-10">
        <div className="tc-container py-4 md:py-16">
          <div
            className={`flex ${layout === '1' ? 'min-h-[640px]' : 'min-h-[580px]'} flex-col md:grid md:min-h-0 ${getGapClasses()} ${getLayoutClasses()}`}
          >
            {/* Content Section */}
            <div className={`flex flex-col justify-center ${getContentOrder()}`}>
              <div className="bg-background-200 -mx-4 -mt-4 p-4 pt-4 md:mx-0 md:mt-0 md:max-w-[380px] md:bg-transparent md:p-0 md:pt-0">
                <h1 className="mb-2 md:mb-4">
                  {firstWord}
                  <br className="md:hidden" /> {restOfHeading}
                </h1>
                {subheading && (
                  <p className="tc-subheading mb-6 hidden text-lg md:block md:max-w-[320px] md:text-xl">{subheading}</p>
                )}

                <div className="mt-2 md:mt-6">
                  <TrustPilotWidget layout={TrustpilotLayouts.MicroReviewCount} variant="light" />
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className={getFormOrder()}>
              <div className="md:max-w-[440px]">
                <LandingPageForm
                  heading=""
                  subHeading=""
                  regLabel={isMobile ? mobileRegLabel : desktopRegLabel}
                  showRegLabel={showRegLabel}
                  showRegToggle={showRegToggle}
                  defaultDurationType={defaultDurationType}
                  daysOnly={daysOnly}
                  useBanner={useBanner}
                  bannerCoverType={bannerCoverType}
                  bannerCoverDuration={bannerCoverDuration}
                  checkboxLabel={checkboxLabel}
                  useCardStyle={useCardStyle}
                  journey={journey}
                  regPlateOnly={regPlateOnly}
                  showDurationPicker={shouldShowDurationPicker}
                  formIntroText={isMobile ? '' : formIntroText}
                  submitButtonText={submitButtonText}
                  submitButtonColor={submitButtonColor}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
